import { appContext } from "../../../../AppContext";
import { UserPicker, IUserPickerUser } from "@iconics/components";
import Helper from "../../../../Common/Helper";
import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { PagedResponse } from "@iconics/core";
import { Delegate, User } from "./DataModels";
import { ApiConstants } from "../../../../Providers.Api/ApiConstants";
import { SerendieSymbol } from "@serendie/symbols";
import { Avatar } from "@serendie/ui";
import { debounceTimeInMilliseconds } from "@iconics/core";
import { StringHelper } from "@iconics/core";
import { Button, ButtonBar, Card, ConfirmDialog, DataGrid, GridContainer, HelperText, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, PageNav, RightButtons, Stack, SymbolButton, TextField, Typography } from "@iconics/components";
import { Guid } from "@iconics/core";

export default class CreateManyDelegates extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private searchDelegatesTimeout: NodeJS.Timeout | null = null;
    private string = new StringHelper();

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            delegatorSearchText: '',
            delegator: null,

            delegatesSearchText: '',
            delegatesError: '',
            delegatesGrid: { rows: [], checkedIds: [] },
            delegatesRefreshToken: Guid.new().toString(),
            assignedDelegates: [],

            showConfirmResetDelegatorDialog: false,
            disableSave: true,
            sections: [],
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcDelegatePermissions_S;
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        const prevAssignedDelegateIds = prevState.assignedDelegates.map(delegate => delegate.id).sort().join(',');
        const assignedDelegateIds = this.state.assignedDelegates.map(delegate => delegate.id).sort().join(',');

        if (prevAssignedDelegateIds === assignedDelegateIds) 
        {
            return;
        }

        const hasErrors = this.assignedDelegatesAndErrors.some(i => i.error);
        const noAssignedDelegates = prevState.assignedDelegates === this.state.assignedDelegates && this.state.assignedDelegates.length === 0;
        const shouldDisableSave = hasErrors || noAssignedDelegates;

        this.setState({ disableSave: shouldDisableSave });
    }

    private async resetDelegator(): Promise<void>
    {
        await this.setStateAsync({
            delegator: null,
            assignedDelegates: [],
        });
        this.checkAssignedDelegates();

    }

    private async loadAssignedDelegates(): Promise<void>
    {
        await this.loading(async () =>
        {
            const delegateData = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.delegates.get<PagedResponse<Delegate[]>>({
                select: Delegate,
                filter: `PrimaryEmail eq '${this.state.delegator?.email ?? ''}'`,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });

            const delegates = delegateData.value.map(i => ({
                id: i.DelegateEmail,
                name: (i.DelegateDisplayName || i.DelegateEmail),
                email: i.DelegateEmail,
            }) as IDelegate);

            await this.setStateAsync({ assignedDelegates: delegates });
            this.checkAssignedDelegates();
        });
    }

    private get assignedDelegatesAndErrors(): IDelegate[]
    {
        const withErrors = this.state.assignedDelegates.map(i =>
        ({
            id: i.id,
            email: i.email,
            name: i.name,
            error: (i.id.toLocaleLowerCase() === this.state.delegator?.email?.toLocaleLowerCase() ? this.labels.funcDelegatorCannotDelegateSelf_L : ''),
            userType: i.userType,
        }));
        return withErrors;
    }

    private checkAssignedDelegates(): void
    {
        const checkedIds = this.state.delegatesGrid.rows
            .map(i => i.id)
            .filter(checkedId => this.state.assignedDelegates.some(i => i.id == checkedId));

        this.setState({ delegatesGrid: { ...this.state.delegatesGrid, checkedIds: checkedIds } });
    }

    private async handleDelegatorChange(user: IUserPickerUser | null): Promise<void>
    {
        await this.setStateAsync({
            delegator: user,
            delegatesError: '',
        });

        if (!user)
        {
            await this.resetDelegator();
            return;
        }
        await this.loadAssignedDelegates();
    }

    private handleDelegatorReset(): void
    {
        this.setState({ showConfirmResetDelegatorDialog: true });
    }

    private async handleDelegatorResetConfirm(): Promise<void>
    {
        await this.resetDelegator();
        this.setState({ showConfirmResetDelegatorDialog: false, delegatorSearchText: '' });
    }

    private async handleDelegatesSearchTextChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): Promise<void>
    {
        clearTimeout(this.searchDelegatesTimeout ?? undefined);
        await this.setStateAsync({ delegatesSearchText: event.target.value });
        this.setState({ delegatesError: '' });
        this.searchDelegatesTimeout = setTimeout(() => this.setState({ delegatesRefreshToken: Guid.new().toString() }), debounceTimeInMilliseconds);
    }

    private async handleDelegatesRequest(request: IGridRowsRequest<IDelegate>): Promise<IGridRowsResponse<IDelegate>>
    {
        const { delegatesSearchText } = this.state;

        const searchTerm = delegatesSearchText.replace(/'/g, "''");

        const users = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.get<PagedResponse<User[]>>({
            tenancy: appContext().tenantId,
            select: User,
            skipToken: request.skipToken,
            top: request.take,
            filter: `contains(PreferredName, '${searchTerm}') or contains(Email, '${searchTerm}')`,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const delegates = users.value.map(i => ({
            id: i.Id,
            name: i.PreferredName ?? '',
            email: i.Email ?? '',
            userType: i.UserType ?? '',
        }) as IDelegate);

        return { rows: delegates, nextSkipToken: users.skipToken };
    }

    private async handleDelegatesGridChange(grid: IGridData<IDelegate>): Promise<void>
    {
        const prevCheckedIds = DataGrid.checkedIdsHash(this.state.delegatesGrid.checkedIds ?? []);
        const checkedIds = DataGrid.checkedIdsHash(grid.checkedIds ?? []);
        await this.setStateAsync({ delegatesGrid: grid });

        if (prevCheckedIds != checkedIds)
        {
            this.handleCheckedDelegatesChange(grid.checkedIds ?? []);
        }
        else
        {
            this.checkAssignedDelegates();
        }
    }

    private handleCheckedDelegatesChange(checkedIds: string[]): void
    {
        if (this.state.delegator == null)
        {
            this.setState({ delegatesError: this.labels.funcMustSelectDelegator_L });
            return;
        }

        // add delegates
        const assignedIds = this.state.assignedDelegates.map(i => i.id);
        const idsToAdd = checkedIds.filter(i => !assignedIds.some(j => j === i));
        const delegatesToAdd = this.state.delegatesGrid.rows.filter(i => idsToAdd.some(j => j === i.id));

        // remove delegates
        const idsToRemove = this.state.delegatesGrid.rows.map(i => i.id).filter(i => assignedIds.some(j => j === i) && !checkedIds.some(j => j === i));
        const delegatesNotRemoved = this.state.assignedDelegates.filter(i => !idsToRemove.some(j => j === i.id));

        // join
        const assignedDelegates = [...delegatesNotRemoved, ...delegatesToAdd];
        this.setState({ assignedDelegates: assignedDelegates });
    }

    private async handleDelegatesDeselect(id: string): Promise<void>
    {
        await this.setStateAsync({ assignedDelegates: this.state.assignedDelegates.filter(i => i.id !== id) });
        this.checkAssignedDelegates();
    }

    private async handleSave(): Promise<void>
    {
        const delegator = this.state.delegator;
        if (!delegator)
        {
            return;
        }

        await this.loading(async () =>
        {
            this.setState({ delegatesError: '' });

            const delegates = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.delegates.get<PagedResponse<Delegate[]>>({
                select: Delegate,
                filter: `PrimaryEmail eq '${delegator.email}'`,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });

            const delegatesEmailsToCreate = this.state.assignedDelegates.filter(i => !delegates.value.some(j => j.DelegateEmail === i.id)).map(i => i.email);
            if (delegatesEmailsToCreate.length > 0)
            {
                await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.delegates.post({
                    tenancy: appContext().tenantId,
                    body: delegatesEmailsToCreate.map(i =>
                    ({
                        Scope: 0,
                        PrimaryEmail: delegator.email,
                        DelegateEmail: i,
                    })),
                });
            }

            const delegateIdsToDelete = delegates.value.filter(i => !this.state.assignedDelegates.some(j => j.id === i.DelegateEmail)).map(i => i.Delegate_Id);
            if (delegateIdsToDelete.length > 0)
            {
                await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.delegates.delete({
                    tenancy: appContext().tenantId,
                    body: delegateIdsToDelete,
                });
            }

            await this.resetDelegator();
        });
    }

    public render(): JSX.Element
    {
        const state = this.state;
        const labels = this.labels;

        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <GridContainer
                            spacing='cards'
                        >
                            <GridItem>
                                <Card
                                    icon={<SerendieSymbol name='user' />}
                                    title={labels.funcDelegateSummary_S}
                                    subTitle={labels.funcAdminDelegateSummary_D}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                                >
                                    <GridContainer>
                                        {
                                            !state.delegator &&
                                            <GridItem>
                                                <UserPicker
                                                    tenantId={appContext().tenantId}
                                                    searchText={this.state.delegatorSearchText}
                                                    label={labels.funcSearchDelegator_S}
                                                    placeholder={labels.funcSearchDelegator_L}
                                                    onChange={async text => this.setState({ delegatorSearchText: text })}
                                                    onUserChange={user => this.handleDelegatorChange(user)}
                                                />
                                            </GridItem>
                                        }
                                        {
                                            state.delegator &&
                                            <>
                                                <GridItem>
                                                    <Typography role='headline' size='small'>{labels.funcDelegator_S}</Typography>
                                                </GridItem>
                                                <GridItem>
                                                    {this.renderUser(state.delegator.displayName, state.delegator.email)}
                                                </GridItem>
                                                <GridItem>
                                                    <Typography role='headline' size='small'>{labels.funcDelegates_S}</Typography>
                                                </GridItem>
                                                {
                                                    state.assignedDelegates.length === 0 &&
                                                    <GridItem>
                                                        <Typography role='body'>{labels.funcNoDelegates_S}</Typography>
                                                    </GridItem>
                                                }
                                                {
                                                    this.assignedDelegatesAndErrors.map(i =>
                                                        <>
                                                            {
                                                                i.error &&
                                                                <GridItem
                                                                    key={`${i.id}_error`}
                                                                >
                                                                    <HelperText error={true}>{i.error}</HelperText>
                                                                </GridItem>
                                                            }
                                                            <GridItem
                                                                key={`${i.id}_user`}
                                                                compact={10}
                                                                expanded={10}
                                                            >
                                                                {this.renderUser(i.name, i.email)}
                                                            </GridItem>
                                                            <GridItem
                                                                key={`${i.id}_close`}
                                                                compact={2}
                                                                expanded={2}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'right',
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                <SymbolButton
                                                                    name='close'
                                                                    aria-label='close'
                                                                    onClick={() => this.handleDelegatesDeselect(i.id)}>
                                                                </SymbolButton>
                                                            </GridItem>
                                                        </>
                                                    )
                                                }
                                            </>
                                        }
                                    </GridContainer>
                                </Card>
                            </GridItem>
                            <GridItem>
                                <Card
                                    icon={<SerendieSymbol name='users' />}
                                    title={labels.funcDelegateYourAccountAccess_S}
                                    subTitle={labels.funcDelegateYourAccountAccess_D}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                                >
                                    <GridContainer>
                                        <GridItem>
                                            <TextField
                                                label={labels.funcSearchDelegates_S}
                                                placeholder={labels.funcSearchDelegates_L}
                                                value={state.delegatesSearchText}
                                                onChange={i => this.handleDelegatesSearchTextChange(i)}
                                                invalid={!!state.delegatesError}
                                                invalidMessage={state.delegatesError}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <DataGrid
                                                columns={[
                                                    {
                                                        columnId: Helper.nameOf<User>('Initials'),
                                                        title: '',
                                                        getCellValue: i => i.name,
                                                        template: i => <Avatar text={this.string.initials(i.value as string).toUpperCase()} />,
                                                        columnWidth: 5,
                                                    },
                                                    {
                                                        columnId: Helper.nameOf<User>('PreferredName'),
                                                        title: this.labels.User_PreferredName,
                                                        getCellValue: i => i.name,
                                                        columnWidth: 45,
                                                        sortable: true,
                                                    },
                                                    {
                                                        columnId: Helper.nameOf<User>('Email'),
                                                        title: this.labels.User_Email,
                                                        getCellValue: i => i.email.toLocaleLowerCase() ?? '',
                                                        columnWidth: 50,
                                                        sortable: true,
                                                    }
                                                ]}
                                                data={this.state.delegatesGrid}
                                                onChange={i => this.handleDelegatesGridChange(i)}
                                                rowId={i => i.id}
                                                onRowsRequest={i => this.handleDelegatesRequest(i)}
                                                showCheckBoxes={true}
                                                refreshToken={this.state.delegatesRefreshToken}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </Card>
                            </GridItem>
                        </GridContainer>
                    </LayoutContent>
                    <LayoutRight>
                        <PageNav
                            sections={this.state.sections}
                        />
                    </LayoutRight>
                    <LayoutFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    disabled={!state.delegator}
                                    onClick={() => this.handleDelegatorReset()}
                                >
                                    {labels.Platform_Delegates_Switch}
                                </Button>
                                <Button
                                    styleType='filled'
                                    disabled={!state.delegator || this.state.disableSave}
                                    onClick={() => this.handleSave()}
                                >
                                    {labels.Platform_CommonAction_Save}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </LayoutFooter>
                </LayoutContainer>
                <ConfirmDialog
                    show={this.state.showConfirmResetDelegatorDialog}
                    header={this.labels.funcSwitchDelegator_S}
                    content={this.labels.funcSwitchDelegator_D}
                    onClose={() => this.setState({ showConfirmResetDelegatorDialog: false })}
                    onConfirm={() => this.handleDelegatorResetConfirm()}
                />
            </>
        );
    }

    private renderUser(fullName: string, email: string): JSX.Element
    {
        return (
            <GridContainer
                spacing="md"
            >
                <GridItem
                    compact='auto'
                >
                    <Avatar text={this.string.initials(fullName).toUpperCase()} />
                </GridItem>
                <GridItem
                    compact='auto'
                >
                    <Stack
                        spacing="none"
                    >
                        <Typography role='body'>{fullName}</Typography>
                        <Typography role='body'>{email.toLocaleLowerCase()}</Typography>
                    </Stack>
                </GridItem>
            </GridContainer>
        );
    }
}

export interface IProps
{
}

export interface IState
{
    delegatorSearchText: string;
    delegator: IUserPickerUser | null;

    delegatesSearchText: string;
    delegatesError: string;
    delegatesGrid: IGridData<IDelegate>;
    delegatesRefreshToken: string;
    assignedDelegates: IDelegate[];

    showConfirmResetDelegatorDialog: boolean;
    disableSave: boolean;
    sections: IPageNavSection[];
}

export interface IDelegate
{
    id: string;
    name: string;
    email: string;
    userType: string;
    error: string;
}
